/**
 * Resolve LMS media URLs for admin: Spaces CDN for uploads/content, API for attendance.
 */

function stripWrappingQuotes(s: string): string {
  let t = s.trim();
  if (
    (t.startsWith("'") && t.endsWith("'")) ||
    (t.startsWith('"') && t.endsWith('"'))
  ) {
    t = t.slice(1, -1).trim();
  }
  return t;
}

function repairConcatenatedAbsoluteUrl(input: string): string {
  const s = stripWrappingQuotes(input);
  const httpAt = Math.max(s.lastIndexOf("http://"), s.lastIndexOf("https://"));
  if (httpAt > 0) {
    const tail = s.slice(httpAt);
    try {
      new URL(tail);
      return tail;
    } catch {
      /* keep original */
    }
  }
  return s;
}

function normalizeHref(url: string): string {
  try {
    return new URL(url).href;
  } catch {
    return url;
  }
}

const DEFAULT_MEDIA_CDN =
  "https://exskilence-desktop-releases.sfo3.cdn.digitaloceanspaces.com";

export function getApiOrigin(): string {
  const raw = stripWrappingQuotes(process.env.REACT_APP_API_BASE_URL || "");
  return raw.replace(/\/+$/, "").replace(/\/api$/i, "").replace(/\/+$/, "").trim();
}

export function getMediaCdnBase(): string {
  const raw = stripWrappingQuotes(process.env.REACT_APP_MEDIA_CDN_BASE || "");
  return (raw || DEFAULT_MEDIA_CDN).replace(/\/+$/, "");
}

/** Spaces key prefix. Set only in staging/dev. Empty in prod → keep /media/uploads on the API. */
export function getMediaSpacesPrefix(): string {
  const raw = stripWrappingQuotes(process.env.REACT_APP_MEDIA_SPACES_PREFIX || "");
  return raw.replace(/^\/+|\/+$/g, "");
}

export function isSpacesMediaHost(url: string): boolean {
  try {
    const host = new URL(url).hostname.toLowerCase();
    return host.endsWith("digitaloceanspaces.com");
  } catch {
    return false;
  }
}

function rewriteMediaPath(pathname: string, search: string, backend: string): string {
  if (pathname.startsWith("/media/uploads/") || pathname.startsWith("/media/content/")) {
    // step 1: staging/dev prefix → CDN/{prefix}/uploads|content/...
    // step 2: prod (empty prefix) → keep the API /media/... URL
    const prefix = getMediaSpacesPrefix();
    if (prefix) {
      const rest = pathname.replace(/^\/media\//, "");
      return normalizeHref(`${getMediaCdnBase()}/${prefix}/${rest}${search}`);
    }
    if (backend) {
      return normalizeHref(`${backend}${pathname}${search}`);
    }
    return "";
  }
  if (pathname.startsWith("/media/attendance/") && backend) {
    return normalizeHref(`${backend}${pathname}${search}`);
  }
  return "";
}

export function resolveMediaUrl(url: string | null | undefined): string {
  if (url == null || url === undefined) return "";
  const raw = String(url).trim().replace(/^@\s*/, "");
  if (!raw || raw.startsWith("data:") || raw.startsWith("blob:")) return raw;

  const trimmed = repairConcatenatedAbsoluteUrl(raw);
  const backend = getApiOrigin();

  try {
    // Already a Spaces/CDN URL — do not rewrite the host back to the API.
    if (/^https?:\/\//i.test(trimmed) && isSpacesMediaHost(trimmed)) {
      return normalizeHref(trimmed);
    }
    const mediaIdx = trimmed.indexOf("/media/");
    if (mediaIdx >= 0) {
      const fromMedia = trimmed.slice(mediaIdx);
      const parsedTail = new URL(fromMedia, `${backend || "https://api.exskilence.com"}/`);
      const pathname = parsedTail.pathname.startsWith("//")
        ? parsedTail.pathname.replace(/^\/+/, "/")
        : parsedTail.pathname;
      const rewritten = rewriteMediaPath(pathname, parsedTail.search, backend);
      if (rewritten) return rewritten;
      if (backend) return normalizeHref(`${backend}${pathname}${parsedTail.search}`);
    }
    if (!backend) return trimmed.startsWith("/") ? trimmed : normalizeHref(trimmed);
    const parsed = new URL(trimmed, `${backend}/`);
    const pathname = parsed.pathname.startsWith("//")
      ? parsed.pathname.replace(/^\/+/, "/")
      : parsed.pathname;
    const rewritten = rewriteMediaPath(pathname, parsed.search, backend);
    if (rewritten) return rewritten;
    return normalizeHref(trimmed);
  } catch {
    return trimmed;
  }
}
